import React, { useContext, useEffect, useRef, useState, useMemo } from 'react'
import { Button, Collapse, Paper, useTheme, Select, MenuItem, Chip, Switch } from '@mui/material'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import delay from 'delay'
import moment from 'moment'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import anime from 'animejs'
import Drawer from '@mui/material/Drawer'
import Tooltip from '@mui/material/Tooltip'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloseIcon from '@mui/icons-material/Close'
import CloudIcon from '@mui/icons-material/Cloud'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import GppMaybeIcon from '@mui/icons-material/GppMaybe'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import ErrorIcon from '@mui/icons-material/Error'
import WatchLaterIcon from '@mui/icons-material/WatchLater'
import SubtitlesIcon from '@mui/icons-material/Subtitles'
import PaymentsIcon from '@mui/icons-material/Payments'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import NewReleasesIcon from '@mui/icons-material/NewReleases'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import DoneAllIcon from '@mui/icons-material/DoneAll'
import ReportProblemIcon from '@mui/icons-material/ReportProblem'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import DeleteIcon from '@mui/icons-material/Delete'
import RefreshIcon from '@mui/icons-material/Refresh'
import CheckIcon from '@mui/icons-material/Check'
import StorageIcon from '@mui/icons-material/Storage'
import ShieldIcon from '@mui/icons-material/Shield'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ReplayIcon from '@mui/icons-material/Replay'
import PlaylistRemoveIcon from '@mui/icons-material/PlaylistRemove'
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep'
import { alpha } from '@mui/material/styles'
import messagesLib from '../../libs/messages'
import { TemplatePreviewBubble } from '../TemplatePreviewBubble'
import { HiperPayPreview } from '../HiperPayPreview'
import { translateMetaError } from '../../libs/metaErrors'
import { ApiDataWrapperContext } from '../../contexts/ApiDataWrapper'
import hipersendService from '../../services/hipersend'
import Text from '../Text'
import CircularProgressCustom from '../CircularProgressCustom'
import ConfirmationModal from '../ConfirmationModal'
import LinearProgressCustom from '../LinearProgressCustom'
import WpBgLight from '../../assets/wp-bg-light.png'
import WpBgDark from '../../assets/wp-bg-dark.png'
import TextFieldCustom from '../TextFieldCustom'
import LinkWithQuery from '../LinkWithQuery'
import metaWhite from '../../assets/meta-white-color.png'
import metaPrimary from '../../assets/meta-primary-color.png'
import PeriodSelector from '../shared/report/PeriodSelector'
import KpiCard from '../shared/report/KpiCard'
import ReportChart from '../shared/report/ReportChart'
import { periodToRange, daysBetween } from '../shared/report/periodRange'

const OFFSET_PAGINATION = 30

// Mapa nome->template (Meta) carregado uma vez, pra o preview do relatório
// renderizar a mensagem como o cliente recebeu sem buscar por mensagem.
const WabaTemplatesContext = React.createContext({})

// Branding (profiles) + banners do HiperPay carregados uma vez, pra reconstruir a
// vitrine do link de pagamento no preview. Tudo leitura; nunca toca no link real.
const HiperPayContext = React.createContext({ profilesById: {}, banners: { top: [], bottom: [] } })

// Filtra os banners ativos dentro da janela de datas e separa por posição,
// ordenados por `order` — mesma regra que a página de pagamento aplica.
function buildActiveBanners(list) {
  const now = Date.now()
  const active = (Array.isArray(list) ? list : [])
    .filter((b) => b && b.isActive !== false)
    .filter((b) => {
      const startOk = !b.startDate || new Date(b.startDate).getTime() <= now
      const endOk = !b.endDate || new Date(b.endDate).getTime() >= now
      return startOk && endOk
    })
    .sort((a, b) => (a.order || 0) - (b.order || 0))

  return {
    top: active.filter((b) => (b.position || 'top') === 'top'),
    bottom: active.filter((b) => b.position === 'bottom'),
  }
}

export default function Reports() {
  const { server, getCredentials, provider, user, usaLinhaDeCredito, saldoInfo, refreshSaldo } = useContext(ApiDataWrapperContext)
  const { api, secret } = getCredentials()
  const theme = useTheme()
  const containerRef = useRef()

  // Período do relatório — afeta cards, gráfico e lista. Presets calculados em BRT.
  const [period, setPeriod] = useState('30d')
  const [customStart, setCustomStart] = useState('')
  const [customEnd, setCustomEnd] = useState('')

  // Range SELECIONADO nos controles (pode estar incompleto no custom).
  const selectedRange = useMemo(() => periodToRange(period, customStart, customEnd), [period, customStart, customEnd])

  // Range APLICADO — o que os dados na tela realmente refletem. Só avança quando
  // o selecionado é válido, então o rótulo nunca descasa dos números exibidos.
  const [applied, setApplied] = useState(() => ({ range: selectedRange, period: '30d' }))
  const rangeRef = useRef(applied.range)
  useEffect(() => { rangeRef.current = applied.range }, [applied])

  const periodLabel = useMemo(() => {
    const p = applied.period
    if (p === 'today') return 'Hoje'
    if (p === '7d') return 'Últimos 7 dias'
    if (p === '30d') return 'Últimos 30 dias'
    if (p === 'custom' && applied.range.startDay && applied.range.endDayInclusive) {
      return `${moment(applied.range.startDay).format('DD/MM/YYYY')} – ${moment(applied.range.endDayInclusive).format('DD/MM/YYYY')}`
    }
    return 'Personalizado'
  }, [applied])

  // Atualização automática (10s), com guarda anti-sobreposição e página corrente.
  const [autoRefresh, setAutoRefresh] = useState(false)
  const autoTickRef = useRef(false)
  const pageRef = useRef(0)

  const [reportData, setReportData] = useState([])
  const [messages, setMessages] = useState([])
  const [withinWorkingHours, setWithinWorkingHours] = useState(false)
  const [statistics, setStatistics] = useState({
    success: undefined,
    error: undefined,
    queue: undefined
  })
  const [page, setPage] = useState(0)
  const [phoneLike, setPhoneLike] = useState('')
  const [messageLike, setMessageLike] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [isLoadingMessages, setLoadingMessages] = useState(false)
  const [isReloading, setIsReloading] = useState(false)
  const [isOpenConfirmationModalClearQueue, setIsOpenConfirmationModalClearQueue] = useState(false)
  const [isOpenConfirmationModalClearReport, setIsOpenConfirmationModalClearReport] = useState(false)
  const [isOpenConfirmationModalResendMessagesWithErrors, setIsOpenConfirmationModalResendMessagesWithErrors] = useState(false)
  const [isOpenConfirmationModalDeleteMessagesWithErrors, setIsOpenConfirmationModalDeleteMessagesWithErrors] = useState(false)
  const [isOpenMessagesFilter, setIsOpenMessagesFilter] = useState(false)
  const [perdiodToDelete, setPeriodToDelete] = useState('1d')
  const [wabaTemplatesByName, setWabaTemplatesByName] = useState({})
  const [hiperPayProfilesById, setHiperPayProfilesById] = useState({})
  const [sfStats, setSfStats] = useState(null)
  const hasSmartFilter = user?.plan?.functionalities?.includes('SmartFilter')
  const [hiperPayBanners, setHiperPayBanners] = useState({ top: [], bottom: [] })

  // Só vale a pena buscar branding/banners do HiperPay se há mensagem com link
  // de pagamento na página atual.
  const hasHiperPayMessage = messages.some((m) => /%PaymentLink%/.test(m?.message || ''))

  // Carrega os templates Meta uma única vez (só pra instâncias Meta Oficial),
  // pra preencher o preview da mensagem com o corpo real do template.
  useEffect(() => {
    if (provider !== 'hiperapi-v3-cloud-official' || !api || !secret) return
    let active = true
    ;(async () => {
      try {
        const res = await hipersendService.notificacoesInteligentes.getWabaSnapshot(api, secret, {})
        const templates = res?.data?.templates || []
        if (!active) return
        const byName = {}
        templates.forEach((t) => {
          if (t?.name) byName[String(t.name).toLowerCase()] = t
        })
        setWabaTemplatesByName(byName)
      } catch (error) {
        console.error(error)
      }
    })()
    return () => { active = false }
  }, [api, secret, provider])

  // Carrega branding (profiles) e banners do HiperPay uma vez, só quando a página
  // tem mensagem com link de pagamento. Falha silenciosa: sem isso, o preview cai
  // num fallback sem branding/banners — nunca quebra a lista.
  useEffect(() => {
    if (!hasHiperPayMessage || !api || !secret) return
    let active = true
    ;(async () => {
      try {
        const [profiles, banners] = await Promise.all([
          hipersendService.hiperpay.getProfiles(api, secret),
          hipersendService.hiperpay.getBanners(api, secret),
        ])
        if (!active) return
        const list = Array.isArray(profiles) ? profiles : (profiles?.profiles || [])
        const byId = {}
        list.forEach((p) => { if (p?._id) byId[String(p._id)] = p })
        setHiperPayProfilesById(byId)
        setHiperPayBanners(buildActiveBanners(Array.isArray(banners) ? banners : (banners?.banners || [])))
      } catch (error) {
        console.error(error)
      }
    })()
    return () => { active = false }
  }, [hasHiperPayMessage, api, secret])

  const handleAnimation = () => {
    anime({
      targets: containerRef.current.children,
      translateY: [10, 0],
      opacity: [0, 1],
      delay: anime.stagger(100),
      scale: [0.99, 1]
    })
  }

  // O expediente (banner de horário) segue vindo do informations; o gráfico
  // agora usa a série por período (getChart).
  const getWorkingHours = async () => {
    try {
      if(!api || !secret) return
      const response = await hipersendService.notificacoesInteligentes.getInformations(api, secret)
      setWithinWorkingHours(response?.officeHourService?.withinWorkingHours ?? false)
    } catch (error) {
      console.error(error)
    }
  }

  // Série diária do período -> formato { data: [[dia, erro, sucesso]], dateTimes }
  // que o ReportChart consome. Preenche dias sem envio com zero (eixo contínuo).
  const getChart = async (r) => {
    try {
      if(!api || !secret) return
      const { dateBegin, dateEnd, startDay, endDayInclusive } = r || rangeRef.current
      const series = await hipersendService.notificacoesInteligentes.getReport(api, secret, { dateBegin, dateEnd })
      const byDay = {}
      ;(series || []).forEach((row) => { byDay[row.day] = row })
      const dayList = daysBetween(startDay, endDayInclusive)
      const data = dayList.map((day) => {
        const row = byDay[day] || {}
        return [day, row.error || 0, row.success || 0]
      })
      setReportData({ data, dateTimes: dayList })
    } catch (error) {
      console.error(error)
      setReportData({ data: [], dateTimes: [] })
    }
  }

  const getMessages = async ({ page = 0, phoneLike = undefined, messageLike = undefined, status = undefined, silent = false } = {}) => {
    try {
      if(!api || !secret) return
      const offset = page * OFFSET_PAGINATION
      const { dateBegin, dateEnd } = rangeRef.current

      // No auto-refresh (silent) pulamos o delay e o spinner pra não piscar a lista.
      if (!silent) {
        setLoadingMessages(true)
        await delay(800)
      }
      const response = await hipersendService.notificacoesInteligentes.getMessages(api, secret, { offset, phoneLike, messageLike, status: status || undefined, dateBegin, dateEnd })
      setMessages(response?.messages || [])
      if (!silent) setLoadingMessages(false)
    } catch (error) {
      console.error(error)
      if (!silent) setLoadingMessages(false)
    }
  }

  const getMessagesStatistics = async (r) => {
    try {
      if(!api || !secret) return
      const { dateBegin, dateEnd } = r || rangeRef.current
      const response = await hipersendService.notificacoesInteligentes.getMessagesStatistics(api, secret, { dateBegin, dateEnd })
      setStatistics(response)
    } catch (error) {
      console.error(error)
    }
  }

  const reloadReport = async () => {
    try {
      setIsReloading(true)
      const r = rangeRef.current
      await Promise.all([
        getWorkingHours(),
        getChart(r),
        getMessagesStatistics(r),
        getMessages({ page, phoneLike, messageLike, status: statusFilter }),
      ])
      if (refreshSaldo) await refreshSaldo() // saldo muda com o consumo — refresca junto
      setIsReloading(false)
    } catch (error) {
      setIsReloading(false)
    }
  }

  const handleClearQueue = async () => {
    try {
      if(!api || !secret) return
      await hipersendService.notificacoesInteligentes.clearQueue(api, secret)
      getMessages({ page, status: statusFilter })
      getMessagesStatistics()
    } catch (error) {
      console.error(error)
    }
  }

  const handleClearReport = async () => {
    try {
      if(!api || !secret) return
      await hipersendService.notificacoesInteligentes.clearReport(api, secret)
      getMessages({ page, status: statusFilter })
      getMessagesStatistics()
    } catch (error) {
      console.error(error)
    }
  }

  const handleResendMessagesWithErrors = async () => {
    try {
      if(!api || !secret) return
      await hipersendService.notificacoesInteligentes.resendMessagesWithErrors(api, secret)
      getMessages({ page, status: statusFilter })
      getMessagesStatistics()
    } catch (error) {
      console.error(error)
    }
  }

  const handleDeleteMessagesWithErrors = async () => {
    try {
      if(!api || !secret) return
      await hipersendService.notificacoesInteligentes.deleteMessagesWithErrors(api, secret, perdiodToDelete)
      getMessages({ page, status: statusFilter })
      getMessagesStatistics()
    } catch (error) {
      console.error(error)
    }
  }

  const paginate = page => {
    getMessages({ page, phoneLike, messageLike, status: statusFilter })

    if(page >= 0) {
      setPage(page)
    }
  }

  const nextPage = () => {
    const currentPage = page + 1
    paginate(currentPage)
  }

  const previousPage = () => {
    const currentPage = page - 1
    paginate(currentPage)
  }

  useEffect(() => {
    handleAnimation()
  }, [])

  // Carga inicial + recarga quando muda a instância OU o período aplicado.
  // Custom incompleto (só uma data) não busca — mantém o período anterior na
  // tela até o usuário escolher início e fim.
  useEffect(() => {
    if (!api || !secret) return
    if (selectedRange.incomplete) return

    // Sincroniza o ref ANTES de buscar: getMessages lê rangeRef.current, e o
    // efeito que espelha `applied` só rodaria no próximo render (buscaria o range
    // anterior na troca de período).
    rangeRef.current = selectedRange
    setApplied({ range: selectedRange, period })
    setPage(0)
    // Volta os KPIs ao skeleton pra não exibir os números do período anterior
    // rotulados com o novo enquanto a busca não resolve. O gráfico atualiza no
    // lugar (sem colapsar) — a série nova entra assim que o getChart responde.
    setStatistics({ success: undefined, error: undefined, queue: undefined })
    getWorkingHours()
    getChart(selectedRange)
    getMessagesStatistics(selectedRange)
    getMessages({ page: 0, phoneLike, messageLike, status: statusFilter })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, secret, selectedRange.dateBegin, selectedRange.dateEnd])

  // pageRef sempre com a página atual (o auto-refresh recarrega a mesma página).
  useEffect(() => { pageRef.current = page }, [page])

  // Auto-refresh a cada 10s: pausa quando a aba está oculta, não sobrepõe
  // chamadas e limpa o intervalo ao desligar/desmontar.
  useEffect(() => {
    if (!autoRefresh || !api || !secret) return
    const tick = async () => {
      if (typeof document !== 'undefined' && document.hidden) return
      if (autoTickRef.current) return
      autoTickRef.current = true
      try {
        const r = rangeRef.current
        await Promise.all([
          getChart(r),
          getMessagesStatistics(r),
          getMessages({ page: pageRef.current, phoneLike, messageLike, status: statusFilter, silent: true }),
          getWorkingHours(),
        ])
      } catch (e) {
        // silencioso — a próxima batida tenta de novo
      } finally {
        autoTickRef.current = false
      }
    }
    const id = setInterval(tick, 10000)
    return () => clearInterval(id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, api, secret, phoneLike, messageLike, statusFilter])

  // Carrega stats do SmartFilter só se o plano inclui a funcionalidade
  useEffect(() => {
    if (!hasSmartFilter || !api || !secret) return
    const days = new Date().getDate()
    hipersendService.smartFilter.getStats(api, secret, { days })
      .then(data => setSfStats(data || null))
      .catch(() => {})
  }, [api, secret, hasSmartFilter])

  return (
    <>
      <MessagesFilterModal
        isOpen={isOpenMessagesFilter}
        onClose={() => setIsOpenMessagesFilter(false)}
        phoneLike={phoneLike}
        messageLike={messageLike}
        statusFilter={statusFilter}
        onFilter={({ phoneLike, messageLike, status }) => {
          setPhoneLike(phoneLike)
          setMessageLike(messageLike)
          setStatusFilter(status)
          setPage(0)
          getMessages({ page: 0, phoneLike, messageLike, status })
        }}
      />
      <ConfirmationModal
        open={isOpenConfirmationModalClearReport}
        title={
          <div className="flex items-center space-x-2">
            <Text component="span">
              <GppMaybeIcon style={{ fontSize: 35, color: theme.palette.primary.main }} />
            </Text>
            <Text component="span" className="font-bold text-xl">
              Limpar mensagens
            </Text>
          </div>
        }
        challengeQuestion="Digite a palavra PROSSEGUIR para prosseguir com a limpeza das mensagens"
        challengeAwnswer="PROSSEGUIR"
        onClose={() => {
          setIsOpenConfirmationModalClearReport(false)
        }}
        onConfirm={handleClearReport}
      >
        <Text className="font-bold">Essa ação é irreversível.</Text>
        <Text>Deseja realmente limpar as mensagens?</Text>
      </ConfirmationModal>
      <ConfirmationModal
        open={isOpenConfirmationModalClearQueue}
        title={
          <div className="flex items-center space-x-2">
            <Text component="span">
              <GppMaybeIcon style={{ fontSize: 35, color: theme.palette.primary.main }} />
            </Text>
            <Text component="span" className="font-bold text-xl">
              Limpar fila
            </Text>
          </div>
        }
        challengeQuestion="Digite a palavra PROSSEGUIR para prosseguir com a limpeza da fila"
        challengeAwnswer="PROSSEGUIR"
        onClose={() => {
          setIsOpenConfirmationModalClearQueue(false)
        }}
        onConfirm={handleClearQueue}
      >
        <Text className="font-bold">Essa ação é irreversível.</Text>
        <Text>Deseja realmente limpar as mensagens que estão na fila?</Text>
      </ConfirmationModal>
      <ConfirmationModal
        open={isOpenConfirmationModalResendMessagesWithErrors}
        title={
          <div className="flex items-center space-x-2">
            <Text component="span">
              <GppMaybeIcon style={{ fontSize: 35, color: theme.palette.primary.main }} />
            </Text>
            <Text component="span" className="font-bold text-xl">
              Reenviar mensagens com erro
            </Text>
          </div>
        }
        challengeQuestion="Digite a palavra PROSSEGUIR para prosseguir com o reenvio das mensagens com erro"
        challengeAwnswer="PROSSEGUIR"
        onClose={() => {
          setIsOpenConfirmationModalResendMessagesWithErrors(false)
        }}
        onConfirm={handleResendMessagesWithErrors}
      >
        <Text>Deseja realmente reenviar as mensagens com erro?</Text>
      </ConfirmationModal>
      <ConfirmationModal
        open={isOpenConfirmationModalDeleteMessagesWithErrors}
        title={
          <div className="flex items-center space-x-2">
            <Text component="span">
              <GppMaybeIcon style={{ fontSize: 35, color: theme.palette.primary.main }} />
            </Text>
            <Text component="span" className="font-bold text-xl">
              Deletar mensagens com erro
            </Text>
          </div>
        }
        challengeQuestion="Digite a palavra PROSSEGUIR para prosseguir com a remoção das mensagens com erro"
        challengeAwnswer="PROSSEGUIR"
        onClose={() => {
          setIsOpenConfirmationModalDeleteMessagesWithErrors(false)
        }}
        onConfirm={handleDeleteMessagesWithErrors}
      > 
        <Text>Período de exclusão:</Text>
         <Select
          className="mt-3 mb-3 w-1/3"
          size="small"
          sx={{
            select: {
              '.MuiOutlinedInput-notchedOutline': {
                borderWidth: '10px !important',
              }
            }
          }}
          labelId="hiperapi-version-label"
          id="hiperapi-version"
          value={perdiodToDelete}
          onChange={event => {
            setPeriodToDelete(event.target.value)
          }}
        >
          <MenuItem value="1d">Últimas 24h</MenuItem>
          <MenuItem value="7d">Últimos 7 dias</MenuItem>
          <MenuItem value="all">Desde o Inicío</MenuItem>
        </Select>
        <Text className="font-bold">Essa ação é irreversível.</Text>
        <Text>Deseja realmente remover as mensagens com erro?</Text>
      </ConfirmationModal>
      <div className="relative px-4">
        <div
          className={clsx('absolute top-0 left-0 w-full h-[400px] ', {
            'bg-opacity-[0.02] bg-white': theme.isDark,
            'bg-opacity-[0.05] bg-gray-800': !theme.isDark
          })}
        />
        <div
          className={clsx('absolute top-0 left-0 w-full h-[550px]', {
            'bg-opacity-[0.02] bg-white': theme.isDark,
            'bg-opacity-[0.05] bg-gray-800': !theme.isDark
          })}
        />
        <div ref={containerRef} className="relative max-w-[1100px] w-full mx-auto py-12 md:py-32">
          {/* Cabeçalho: identidade da instância + seletor de período + auto-refresh */}
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
            <div className="flex items-center gap-3 min-w-0">
              <div
                className="rounded-xl flex items-center justify-center shrink-0"
                style={{ width: 46, height: 46, background: theme.isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' }}
              >
                <StorageIcon style={{ fontSize: 24, opacity: 0.6 }} />
              </div>
              <div className="min-w-0">
                {
                  !provider
                  ?
                  <span className="block bg-gray-500 opacity-[0.2] h-[16px] w-[80px]" />
                  :
                  provider === 'hiperapi-v3-cloud-official'
                  ?
                  <div className="flex items-center space-x-2">
                    <Text className="text-xs font-semibold opacity-50 uppercase tracking-wider">API Oficial</Text>
                    <img src={theme.isDark ? metaWhite : metaPrimary} className="w-[52px]" />
                  </div>
                  :
                  <Text className="text-xs font-semibold opacity-50 uppercase tracking-wider">API</Text>
                }
                <Text className="text-sm md:text-lg font-bold truncate">{api}</Text>
                <Text className="text-xs opacity-45 flex items-center gap-1">
                  <span>Servidor</span>
                  {server !== '' ? <span className="font-semibold">{server}</span> : <span className="inline-block h-[12px] w-[16px] bg-current opacity-20 rounded" />}
                </Text>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:items-center gap-x-5 gap-y-3">
              <PeriodSelector
                period={period}
                customStart={customStart}
                customEnd={customEnd}
                incomplete={selectedRange.incomplete}
                onChange={({ period: p, customStart: cs, customEnd: ce }) => {
                  setPeriod(p)
                  setCustomStart(cs || '')
                  setCustomEnd(ce || '')
                }}
              />
              <label className="flex items-center gap-1 cursor-pointer select-none shrink-0">
                <Switch size="small" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
                <AutorenewIcon style={{ fontSize: 16, color: autoRefresh ? theme.palette.primary.main : 'currentColor', opacity: autoRefresh ? 1 : 0.5 }} />
                <Text className="text-xs font-medium opacity-70">Atualizar a cada 10s</Text>
              </label>
            </div>
          </div>

          {/* KPIs do período */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <KpiCard
              label="Enviados"
              accent="#059669"
              loading={statistics.success === undefined}
              value={statistics.success}
              hint={periodLabel}
              icon={<CheckCircleIcon style={{ fontSize: 22 }} />}
            />
            <KpiCard
              label="Na fila"
              accent="#f59e0b"
              loading={statistics.queue === undefined}
              value={statistics.queue}
              hint="aguardando envio"
              icon={<WatchLaterIcon style={{ fontSize: 22 }} />}
            />
            <KpiCard
              label="Com erro"
              accent="#eb3c3c"
              loading={statistics.error === undefined}
              value={statistics.error}
              hint={periodLabel}
              icon={<ReportProblemIcon style={{ fontSize: 22 }} />}
            />
          </div>
          {/* Resumo SmartFilter — só aparece pra planos com acesso */}
          {hasSmartFilter && sfStats && sfStats.totals && (
            <div
              className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 px-4 py-2.5 rounded-xl"
              style={{
                background: alpha(theme.palette.primary.main, 0.05),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`
              }}
            >
              <div className="flex items-center gap-1.5">
                <ShieldIcon style={{ fontSize: 15, color: theme.palette.primary.main, opacity: 0.7 }} />
                <Text className="text-xs font-semibold opacity-60">SmartFilter</Text>
              </div>
              <div className="flex items-center gap-1">
                <Text className="text-xs opacity-45">Filtradas</Text>
                <Text className="text-sm font-bold">{(sfStats.totals.allowed || 0).toLocaleString('pt-BR')}</Text>
              </div>
              <div className="flex items-center gap-1">
                <Text className="text-xs opacity-45">Bloqueadas</Text>
                <Text className="text-sm font-bold" style={{ color: theme.palette.error.main }}>{(sfStats.totals.blocked || 0).toLocaleString('pt-BR')}</Text>
              </div>
              {(sfStats.totals.delayed || 0) > 0 && (
                <div className="flex items-center gap-1">
                  <Text className="text-xs opacity-45">Atrasadas</Text>
                  <Text className="text-sm font-bold" style={{ color: theme.palette.warning.main }}>{sfStats.totals.delayed.toLocaleString('pt-BR')}</Text>
                </div>
              )}
              {usaLinhaDeCredito && saldoInfo && (
                <div className="flex items-center gap-1 ml-auto">
                  <Text className="text-xs opacity-45">Saldo</Text>
                  <Text
                    className="text-sm font-extrabold"
                    style={{ color: Number(saldoInfo.saldo) >= 0 ? theme.palette.success.main : theme.palette.error.main }}
                  >
                    {Number(saldoInfo.saldo || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                  </Text>
                </div>
              )}
              {/* Linha de crédito ativa mas sem pacote/marco contratado: não há
                  saldo real a exibir. Mostramos o estado em vez de estimar um valor
                  monetário pela contagem de liberadas (que não reflete o que será cobrado). */}
              {usaLinhaDeCredito && !saldoInfo && (
                <div className="flex items-center gap-1 ml-auto">
                  <Text className="text-xs opacity-45">Saldo</Text>
                  <Text className="text-sm font-semibold" style={{ color: theme.palette.text.secondary }}>
                    Sem pacote contratado
                  </Text>
                </div>
              )}
            </div>
          )}

          <div className="mt-12">
            <Text className="">DESEMPENHO DE ENVIOS</Text>
            <Collapse in={!!reportData.data} timeout="auto" unmountOnExit>
              {
                reportData.data &&
                (() => {
                  const rows = reportData.data || []
                  const succ = rows.reduce((a, r) => a + (r[2] || 0), 0)
                  const err = rows.reduce((a, r) => a + (r[1] || 0), 0)
                  const total = succ + err
                  const rate = total > 0 ? ((succ / total) * 100).toFixed(1) : '0'
                  return (
                    <ReportChart
                      title="Envios por dia"
                      periodLabel={periodLabel}
                      categories={reportData.dateTimes || []}
                      series={[
                        { name: 'Enviados com sucesso', color: '#059669', data: rows.map((r) => r[2]) },
                        { name: 'Erros de envio', color: '#eb3c3c', data: rows.map((r) => r[1]) },
                      ]}
                      headerStat={{ label: 'taxa de sucesso', value: `${rate}%`, good: parseFloat(rate) >= 90 }}
                      unitLabel="mensagens"
                      exportFilename="relatorio-envios"
                    />
                  )
                })()
              }
            </Collapse>
          </div>
          {/* Ações rápidas — construtiva à esquerda, destrutivas à direita */}
          <div className="mt-12">
            <Text className="">AÇÕES</Text>
            <Paper
              elevation={0}
              className="mt-2"
              sx={{ borderRadius: '14px', border: theme.isDark ? '1px solid #334155' : '1px solid #e2e8f0', overflow: 'hidden' }}
            >
              <div className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <Button
                  onClick={() => setIsOpenConfirmationModalResendMessagesWithErrors(true)}
                  startIcon={<ReplayIcon />}
                  variant="outlined"
                  sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
                >
                  Reenviar com erro
                </Button>
                <div className="hidden sm:block flex-1" />
                <div className="flex flex-wrap items-center gap-x-1 gap-y-2">
                  <Button
                    onClick={() => setIsOpenConfirmationModalClearQueue(true)}
                    startIcon={<PlaylistRemoveIcon />}
                    color="inherit"
                    sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, opacity: 0.75 }}
                  >
                    Limpar fila
                  </Button>
                  <Button
                    onClick={() => setIsOpenConfirmationModalDeleteMessagesWithErrors(true)}
                    startIcon={<DeleteSweepIcon />}
                    sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, color: '#eb3c3c' }}
                  >
                    Apagar com erro
                  </Button>
                  <Button
                    onClick={() => setIsOpenConfirmationModalClearReport(true)}
                    startIcon={<DeleteIcon />}
                    sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600, color: '#eb3c3c' }}
                  >
                    Limpar mensagens
                  </Button>
                </div>
              </div>
            </Paper>
          </div>
          <div className="mt-12">
            <div className="md:flex justify-between items-end">
              <div>
                <Text>MENSAGENS</Text>
                <Text className="text-sm">Mostrando {messages.length} mensagens na página {page + 1}</Text>
              </div>
              <div className="flex items-center space-x-4 mt-5 md:mt-0">
                <button
                  className="flex items-center space-x-2"
                  style={{ opacity: isReloading ? 0.3 : 1 }}
                  disabled={isReloading}
                  onClick={reloadReport}
                >
                  {
                    isReloading
                    ? <CircularProgressCustom size={20} />
                    : <Text component="span"><RefreshIcon /></Text>
                  }
                  <Text component="span">Atualizar relatório</Text>
                </button>
                <button
                  className="flex items-center space-x-2"
                  style={{ opacity: isReloading ? 0.3 : 1 }}
                  disabled={isReloading}
                  onClick={() => setIsOpenMessagesFilter(true)}
                >
                  {
                    isReloading
                    ? <CircularProgressCustom size={20} />
                    : <Text component="span"><FilterAltIcon /></Text>
                  }
                  <span className="flex items-center space-x-1">
                    <Text component="span">Filtros</Text>
                    {
                      (phoneLike || messageLike || statusFilter)
                      ? <span
                          className="text-xs font-black text-white px-1.5 py-0.5 rounded-full"
                          style={{ backgroundColor: '#059669' }}
                        >ATIVO</span>
                      : <span
                          className="text-xs font-black text-white px-1 text-white"
                          style={{ backgroundColor: theme.palette.primary.main }}
                        >NOVO</span>
                    }
                  </span>
                </button>
              </div>
            </div>
            {
              !withinWorkingHours &&
              <div className="my-2">
                <div className={clsx('bg-red-600 bg-opacity-[1] rounded p-2 flex space-x-2')}>
                  <Text component="span">
                    <AccessTimeIcon className="text-white" style={{ fontSize: 30 }} />
                  </Text>
                  <Text className="pt-1 text-white">
                    Essa instância não está no seu expediente e horário de trabalho e não fará envios no momento. <LinkWithQuery to="/messages/scheduler" className="font-bold">Configurar expediente</LinkWithQuery>
                  </Text>
                </div>
              </div>
            }
            <div className="relative mt-2">
              <Collapse in={isReloading || isLoadingMessages} timeout="auto" unmountOnExit>
                <div className="absolute top-0 left-0 w-full">
                  <LinearProgressCustom />
                </div>
              </Collapse>
              <Collapse in={messages.length === 0 && !isLoadingMessages} timeout="auto" unmountOnExit>
                <div className="py-28 flex flex-col items-center justify-center rounded">
                  <img src="/assets/images/cat-sleeping-gray-001.svg" className="opacity-[0.3]" />
                  <Text className="mt-2 opacity-[0.5]">Não há mensagens por aqui</Text>
                  <Link to="/messages/send" className="mt-4 flex items-center space-x-2">
                    <AutoAwesomeIcon style={{ fontSize: 35, color: theme.palette.primary.main }} />
                    <Text component="span" className="font-bold">
                      ENVIAR PRIMEIRA MENSAGEM
                    </Text>
                  </Link>
                </div>
              </Collapse>
              <Paper elevation={5}>
                <div>
                  <WabaTemplatesContext.Provider value={wabaTemplatesByName}>
                  <HiperPayContext.Provider value={{ profilesById: hiperPayProfilesById, banners: hiperPayBanners }}>
                  <MessageListContainer>
                    {
                      messages.map((message, index) => (
                        <MessageListItem
                          key={message._id}
                          message={message}
                          onResendMessage={() => {
                            getMessages({ page })
                            getMessagesStatistics()
                          }}
                          onDeleteMessage={() => {
                            getMessages({ page })
                            getMessagesStatistics()
                          }}
                        />
                      ))
                    }
                  </MessageListContainer>
                  </HiperPayContext.Provider>
                  </WabaTemplatesContext.Provider>
                </div>
              </Paper>
              <div className="mt-4 flex items-center space-x-4">
                <button
                  className={clsx('p-1 rounded-full bg-opacity-[0.1]', {
                    'bg-white text-white': theme.isDark,
                    'bg-gray-800 text-black': !theme.isDark,
                    'opacity-[0.3]': page === 0
                  })}
                  onClick={previousPage}
                  disabled={page === 0}
                >
                  <ChevronLeftIcon />
                </button>
                <button
                  className={clsx('p-1 rounded-full bg-opacity-[0.1]', {
                    'bg-white text-white': theme.isDark,
                    'bg-gray-800 text-black': !theme.isDark
                  })}
                  onClick={nextPage}
                >
                  <ChevronRightIcon />
                </button>
                <Text className="text-xs font-black">Página {page + 1}</Text>
                {
                  isLoadingMessages &&
                  <CircularProgressCustom size={20} />
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

function MessageListContainer({ children }) {
  return (
    <ol className="divide-y divide-[#8a8a8a37]">
      {children}
    </ol>
  )
}

function MessageListItem({ message, onResendMessage, onDeleteMessage }) {
  const { getCredentials } = useContext(ApiDataWrapperContext)
  const { api, secret } = getCredentials()
  const theme = useTheme()
  const [isExpanded, setIsExpanded] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [file, setFile] = useState(messagesLib.getFileFromMessage(message?.message || '') || undefined)
  const [messageFormatted, setMessageFormatted] = useState('')
  const [messageToResend, setMessageToResend] = useState(undefined)
  const [messageToDelete, setMessageToDelete] = useState(undefined)
  const [isOpenConfirmationModalResendMessage, setIsOpenConfirmationModalResendMessage] = useState(false)
  const [isOpenConfirmationModalDeleteMessage, setIsOpenConfirmationModalDeleteMessage] = useState(false)

  const handleResendMessage = async () => {
    try {
      setIsLoading(true)
      await delay(1000)
      await hipersendService.notificacoesInteligentes.resendMessage(messageToResend._id, api, secret)
      setMessageToResend(undefined)
      onResendMessage()
    } catch (error) {
      console.error(error)
      setMessageToResend(undefined)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDeleteMessage = async () => {
    try {
      await hipersendService.notificacoesInteligentes.deleteMessage(messageToDelete._id, api, secret)
      setMessageToDelete(undefined)
      onDeleteMessage()
    } catch (error) {
      console.error(error)
      setMessageToDelete(undefined)
    }
  }

  useEffect(() => {
    if(!messageToDelete) return
    setIsOpenConfirmationModalDeleteMessage(true)
  }, [messageToDelete])

  useEffect(() => {
    setMessageFormatted(messagesLib.formatMessage(message.message, { link: message.link }))
  }, [])

  useEffect(() => {
    if(!messageToResend) return
    handleResendMessage()
  }, [messageToResend])

  return (
    <>
      <ConfirmationModal
        open={isOpenConfirmationModalResendMessage}
        title={
          <div className="flex items-center space-x-2">
            <Text component="span">
              <GppMaybeIcon style={{ fontSize: 35, color: theme.palette.primary.main }} />
            </Text>
            <Text component="span" className="font-bold text-xl">
              Reenviar mensagem
            </Text>
          </div>
        }
        challengeQuestion="Digite a palavra REENVIAR para prosseguir com o reenvio da mensagem"
        challengeAwnswer="REENVIAR"
        onClose={() => {
          setIsOpenConfirmationModalResendMessage(false)
          setMessageToResend(undefined)
        }}
        onConfirm={handleResendMessage}
      >
        <Text>Deseja realmente reenviar essa mensagem?</Text>
      </ConfirmationModal>
      <ConfirmationModal
        open={isOpenConfirmationModalDeleteMessage}
        title={
          <div className="flex items-center space-x-2">
            <Text component="span">
              <GppMaybeIcon style={{ fontSize: 35, color: theme.palette.primary.main }} />
            </Text>
            <Text component="span" className="font-bold text-xl">
              Deletar mensagem
            </Text>
          </div>
        }
        challengeQuestion="Digite a palavra DELETAR para prosseguir com a remoção da mensagem"
        challengeAwnswer="DELETAR"
        onClose={() => {
          setIsOpenConfirmationModalDeleteMessage(false)
          setMessageToDelete(undefined)
        }}
        onConfirm={handleDeleteMessage}
      >
        <Text className="font-bold">Essa ação é irreversível.</Text>
        <Text>Deseja realmente deletar essa mensagem?</Text>
      </ConfirmationModal>
      <li className="relative">
        <div className="absolute top-0 left-0 w-full">
          <Collapse in={isLoading} timeout="auto" unmountOnExit>
            <LinearProgressCustom />
          </Collapse>
        </div>
        <div className="p-4 grid grid-cols-12 gap-4">
          <div className="col-span-6 md:col-span-2 flex space-x-1">
            <button className="h-[40px] w-[40px]" onClick={() => setIsExpanded(!isExpanded)}>
              <ExpandMoreIcon />
            </button>
            <button
              className={clsx('bg-opacity-[0.03] rounded-full h-[40px] w-[40px]', {
                'bg-white': theme.isDark,
                'bg-gray-800': !theme.isDark
              })}
            >
              {
                message.status === 'success' && message.ack === 3
                ?
                <Tooltip title="Mensagem lida" style={{ color: theme.palette.primary.main }}>
                  <DoneAllIcon />
                </Tooltip>
                :
                message.status === 'success' && message.ack === 2
                ?
                <Tooltip title="Recebida no dispositivo">
                  <DoneAllIcon className="opacity-[0.5]" />
                </Tooltip>
                :
                message.status === 'success' && message.ack === 1
                ?
                <Tooltip title="Recebida no servidor Meta">
                  <CheckIcon className="opacity-[0.5]" />
                </Tooltip>
                :
                message.status === 'erro'
                ?
                <Tooltip title="Com erro">
                  <ReportProblemIcon className={theme.isDark ? 'text-white' : 'text-red-600'} />
                </Tooltip>
                :
                message.status === 'queue'
                ?
                <Tooltip title="Em fila">
                  <WatchLaterIcon className="opacity-[0.6]" />
                </Tooltip>
                :
                message.status === 'antiban_delayed'
                ?
                <Tooltip title={`Adiada pelo anti-ban (teto diário/aquecimento) — reenvio automático${message.sendAt ? ' a partir de ' + moment(message.sendAt).format('DD/MM [às] HH:mm') : ''}`}>
                  <WatchLaterIcon className="text-amber-500" />
                </Tooltip>
                :
                message.status === 'suppressed'
                ?
                <Tooltip title="Não enviada — destinatário na lista de supressão (opt-out)">
                  <GppMaybeIcon className="opacity-[0.7]" />
                </Tooltip>
                :
                <Tooltip title="Não identificado">
                  <NewReleasesIcon />
                </Tooltip>
              }
            </button>
            <Collapse in={!!file} timeout="auto" unmountOnExit>
              <button className={clsx('bg-opacity-[0.03] rounded-full h-[40px] w-[40px]', {
                  'bg-white': theme.isDark,
                  'bg-gray-800': !theme.isDark
                })}>
                  {
                    message?.file?.url
                    ? // s3 file
                    <CloudIcon className="opacity-[0.5]" />
                    : // user file
                    <InsertDriveFileIcon className="opacity-[0.5]" />
                  }
              </button>
            </Collapse>
          </div>
          <div className="hidden md:block col-span-3">
            <Text className="text-xs font-light">{ message.phone.length > 16 ? 'Grupo' : 'Telefone' }</Text>
            <Text>{message.phone}</Text>
          </div>
          <div className="hidden md:block col-span-3">
            <Text className="text-xs font-light">Criado em</Text>
            <Text>{moment(message.created_at).format('DD/MM/YYYY [às] HH:mm:ss')}</Text>
          </div>
          <div className="col-span-6 md:col-span-4 flex space-x-1 justify-end">
            <button
              className="bg-white disabled:opacity-[0.3] bg-opacity-[0.1] rounded-full h-[40px] w-[40px]"
              disabled={isLoading}
              onClick={() => setMessageToResend(message)}
            >
              <RefreshIcon />
            </button>
            <button
              className="bg-white disabled:opacity-[0.3] bg-opacity-[0.1] rounded-full h-[40px] w-[40px]"
              disabled={isLoading}
              onClick={() => setMessageToDelete(message)}
            >
              <DeleteIcon />
            </button>
          </div>
          <div className="block md:hidden col-span-12 grid grid-cols-2">
            <div>
              <Text className="text-xs font-light">Telefone</Text>
              <Text>{message.phone}</Text>
            </div>
            <div>
              <Text className="text-xs font-light">Criado em</Text>
              <Text>{moment(message.created_at).format('DD/MM/YYYY [às] HH:mm:ss')}</Text>
            </div>
          </div>
          <div className="col-span-12">
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              <div>
                {
                  message.status === 'erro' &&
                  <MessageErrorBox erro={message.erro} />
                }
                {
                  message.status === 'antiban_delayed' &&
                  <div className="bg-amber-500 text-white p-1 flex space-x-2">
                    <WatchLaterIcon />
                    <span>
                      Adiada pelo anti-ban (teto diário ou aquecimento do número). Reenvio automático
                      {message.sendAt ? ` a partir de ${moment(message.sendAt).format('DD/MM/YYYY [às] HH:mm')}` : ''}.
                    </span>
                  </div>
                }
                {
                  message.status === 'suppressed' &&
                  <div className="bg-slate-600 text-white p-1 flex space-x-2">
                    <GppMaybeIcon />
                    <span>Não enviada: o destinatário está na lista de supressão (pediu para não receber / opt-out).</span>
                  </div>
                }
                <div className="py-8">
                  <div className="grid grid-cols-3">
                    <div>
                      {
                        message.ack >= 1
                        ? <ProgressDone />
                        : <ProgressWaiting />
                      }
                      <Text className="font-bold text-center pt-6">
                        Recebida no servidor
                      </Text>
                    </div>
                    <div>
                      {
                        message.ack >= 2
                        ? <ProgressDone />
                        : <ProgressWaiting />
                      }
                      <Text className="hidden md:block font-bold text-center pt-6">
                        Mensagem recebida no device
                      </Text>
                      <Text className="block md:hidden font-bold text-center pt-6">
                        Recebida
                      </Text>
                      {
                        message.receivedAt &&
                        <Text className="text-center">
                          {moment(message.receivedAt).format('DD/MM/YYYY [às] HH:mm:ss')}
                        </Text>
                      }
                    </div>
                    <div>
                      {
                        message.ack >= 3
                        ? <ProgressDone />
                        : <ProgressWaiting />
                      }
                      <Text className="hidden md:block font-bold text-center pt-6">
                        Mensagem lida
                      </Text>
                      <Text className="block md:hidden font-bold text-center pt-6">
                        Lida
                      </Text>
                      {
                        message.readAt &&
                        <Text className="text-center">
                          {moment(message.readAt).format('DD/MM/YYYY [às] HH:mm:ss')}
                        </Text>
                      }
                    </div>
                  </div>
                </div>
                {
                  file &&
                  <div className={clsx('flex items-center space-x-2 mb-4 p-2 rounded bg-opacity-[0.1]', {
                    'bg-white': theme.isDark,
                    'bg-gray-800': !theme.isDark
                  })}>
                    <InsertDriveFileIcon style={{ fontSize: 30, opacity: 0.5 }} />
                    <div>
                      <span className="block font-ligh text-sm">{file.filename}</span>
                      <a className="block font-medium" href={sanitizeSgpFileUrl(file.url)} target="_blank" rel="noreferrer">{sanitizeSgpFileUrl(file.url)}</a>
                    </div>
                  </div>
                }
                <Paper elevation={8}>
                  <div
                    className="p-4 md:p-12 space-y-6"
                    style={{
                      backgroundImage: theme.isDark ? `url(${WpBgDark})` : `url(${WpBgLight})`
                    }}
                  >
                    {/* message */}
                    { messageFormatted && <MessageSimple message={message} /> }
                    {/* file */}
                    { file && <MessageFile file={file} /> }
                  </div>
                </Paper>
              </div>
            </Collapse>
          </div>
        </div>
      </li>
    </>
  )
}

function Message({ children, isFile }) {
  const theme = useTheme()

  return (
    <div
      className={clsx('h-full table rounded p-2 md:p-4 whitespace-pre-line', {
        'shadow-md bg-[#005c4bff]': theme.isDark,
        'bg-[#ecebeb]': !theme.isDark,
        'md:max-w-[60%]': !isFile,
        'md:max-w-[40%]': isFile
      })}
    >
      {children}
    </div>
  )
}

function MessageSimple({ message }) {
  const paymentLinkThumbnail = messagesLib.getPaymentLinkThumbnail(message.message)
  const hiperPayData = messagesLib.getHiperPayData(message.message)
  const { profilesById, banners } = useContext(HiperPayContext)

  const bubble = (
    <div>
      <Message>
        {
          paymentLinkThumbnail &&
          <div className="w-full mb-2">
            <img
              src={paymentLinkThumbnail}
              className="rounded-md"
            />
          </div>
        }
        {
          message.isWhatsappMessageTemplate
          ?
          <MessageTemplatePreviewHiperApiV3 message={message} />
          :
          messagesLib.formatMessage(message.message, { link: message.link })
        }
      </Message>
      {
        message.isWhatsappMessageTemplate &&
        <MessageSimpleShowPayloadHiperApiV3 message={message} />
      }
    </div>
  )

  // Sem link de pagamento: só a mensagem, como antes.
  if (!hiperPayData) return bubble

  // Com link HiperPay: mensagem à esquerda, vitrine reconstruída à direita
  // (empilha no mobile). A vitrine usa branding/banners do contexto.
  const profile = hiperPayData.profileId ? profilesById[String(hiperPayData.profileId)] : undefined

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
      <div>
        <Text className="font-light text-xs mb-2">MENSAGEM WHATSAPP</Text>
        {bubble}
      </div>
      <div>
        <Text className="font-light text-xs mb-2">PÁGINA DE PAGAMENTO (HIPERPAY)</Text>
        <HiperPayPreview data={hiperPayData} profile={profile} banners={banners} link={message.link} />
      </div>
    </div>
  )
}

// Mostra primeiro a EXPERIÊNCIA de quem recebe. Quando o template Meta está no
// snapshot, renderiza a bolha rica (igual à Home) preenchida com os dados que
// foram enviados ao cliente. Sem o template, cai numa bolha simples com os dados.
// Sem dados estruturados, cai no comportamento antigo (nome do template). O
// payload completo segue disponível no "VER PAYLOAD".
function MessageTemplatePreviewHiperApiV3({ message }) {
  const templatesByName = useContext(WabaTemplatesContext)
  const data = messagesLib.getWhatsappCloudApiTemplateData(message.message)

  if (!data || !data.templateName) {
    return <MessageSimpleTemplateHiperApiV3 message={message} />
  }

  const template = templatesByName?.[String(data.templateName).toLowerCase()]

  if (template) {
    return (
      <div>
        <Text className="font-light text-xs mb-2">COMO O CLIENTE RECEBE</Text>
        <TemplatePreviewBubble
          template={template}
          values={data.variables}
          buttonValues={data.buttons}
          paymentLink={message.link}
        />
        <Text className="block text-[10px] opacity-50 mt-1">Template: {data.templateName}</Text>
      </div>
    )
  }

  return <MessageTemplateDataBubble data={data} />
}

// Fallback (template não encontrado no snapshot): bolha simples com os dados
// enviados, rótulos inferidos, valor em destaque e botão de pagamento.
function MessageTemplateDataBubble({ data }) {
  const labels = messagesLib.getTemplateVariableLabels(data.templateName, data.variables.length)
  const valorIndex = labels.findIndex((label) => label === 'Valor')

  return (
    <div>
      <Text className="font-light text-xs mb-2">COMO O CLIENTE RECEBE</Text>
      <div className="rounded-2xl overflow-hidden border border-[#0000000d]" style={{ backgroundColor: '#202c33' }}>
        <div className="px-4 py-3 space-y-2">
          {data.variables.map((value, index) => {
            const isValor = index === valorIndex
            return (
              <div key={index} className="flex flex-col">
                <span className="text-[10px] uppercase tracking-wide text-gray-400">
                  {labels[index]}
                </span>
                <span className={clsx('text-white break-words', { 'text-lg font-black text-emerald-400': isValor })}>
                  {isValor ? `R$ ${value}` : value}
                </span>
              </div>
            )
          })}
          {valorIndex === -1 && data.price && (
            <div className="flex flex-col">
              <span className="text-[10px] uppercase tracking-wide text-gray-400">Valor</span>
              <span className="text-lg font-black text-emerald-400">{data.price}</span>
            </div>
          )}
        </div>
        {data.hasPaymentButton && (
          <div className="border-t border-[#ffffff1a] flex items-center justify-center space-x-2 py-3" style={{ color: '#53bdeb' }}>
            <PaymentsIcon style={{ fontSize: 18 }} />
            <span className="text-sm font-medium">Pagar minha fatura agora</span>
          </div>
        )}
      </div>
    </div>
  )
}

function MessageSimpleTemplateHiperApiV3({ message }) {
  const theme = useTheme()
  const templateName = messagesLib.getWhatsappCloudApiTemplateName(message.message)
  return (
    <div>
      <Text className="font-light text-xs">TEMPLATE ENVIADO</Text>
      {
        templateName
        ? <Text>{ templateName }</Text>
        :
        <div className="flex items-center space-x-2 pt-1">
          <ErrorIcon className={clsx({ 'text-red-600': !theme.isDark })} />
          <Text className={clsx('font-black', { 'text-red-600': !theme.isDark })}>Não identificado</Text>
        </div>
      }
    </div>
  )
}

function MessageSimpleShowPayloadHiperApiV3({ message }) {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [isCopied, setIsCopied] = useState(false)

  return (
    <>
    <Drawer
      anchor="right"
      open={isModalOpen}
      onClose={() => setIsModalOpen(false)}
    >
      <div className="w-[750px] h-screen p-8">
        <div className="mb-4 flex justify-between">
          <button onClick={() => setIsModalOpen(false)} className="flex items-center space-x-2">
            <ChevronLeftIcon />
            <span className="text-xs font-medium">FECHAR</span>
          </button>
          <CopyToClipboard
            text={message.message}
            onCopy={async () => {
              setIsCopied(true)
              await delay(1000)
              setIsCopied(false)
            }}
          >
            <button
              disabled={isCopied}
              className="flex items-center space-x-2"
            >
              {
                isCopied
                ? <CheckIcon style={{ fontSize: 18 }} />
                : <ContentCopyIcon style={{ fontSize: 18 }} />
              }
              <span className="text-xs font-medium">
                {
                  isCopied
                  ? 'COPIADO'
                  : 'COPIAR CONTEÚDO'
                }
              </span>
            </button>
          </CopyToClipboard>
        </div>
        <textarea className="w-full h-full bg-[#7e7e7e12] p-4 font-medium" disabled>{ message?.message }</textarea>
      </div>
    </Drawer>
    <div className="px-1 py-1">
      <button
        onClick={() => setIsModalOpen(true)}
        className="text-xs font-medium flex items-center space-x-2"
      >
        <SubtitlesIcon />
        <span>VER PAYLOAD</span>
      </button>
    </div>
    </>
  )
}

// Caixa de erro do relatório. Em instâncias Meta Oficial, traduz o erro da Meta
// para uma explicação em pt-br com ação sugerida; o texto técnico original fica
// atrás de "ver detalhe técnico". Em conexões Baileys (erro já em pt-br do
// backend), mostra o texto como está.
function MessageErrorBox({ erro }) {
  const { provider } = useContext(ApiDataWrapperContext)
  const [showTech, setShowTech] = useState(false)
  const isMeta = provider === 'hiperapi-v3-cloud-official'
  const friendly = isMeta ? translateMetaError(erro) : null

  if (!friendly) {
    return (
      <div className="bg-red-600 text-white p-1 flex space-x-2">
        <ReportProblemIcon />
        <span>{erro?.text}</span>
      </div>
    )
  }

  return (
    <div className="bg-red-600 text-white p-2">
      <div className="flex items-start space-x-2">
        <ReportProblemIcon />
        <div className="flex-1">
          <p className="font-black">{friendly.titulo}</p>
          <p className="text-sm opacity-90">{friendly.explicacao}</p>
          {friendly.acao && (
            <p className="text-sm mt-1">
              <span className="font-bold">O que fazer:</span> {friendly.acao}
            </p>
          )}
          {friendly.tecnico && (
            <>
              <button
                onClick={() => setShowTech((v) => !v)}
                className="text-xs underline mt-1 opacity-80"
              >
                {showTech ? 'ocultar detalhe técnico' : 'ver detalhe técnico'}
              </button>
              {showTech && (
                <p className="text-xs mt-1 opacity-80 break-words">
                  {friendly.code ? `[${friendly.code}] ` : ''}
                  {friendly.tecnico}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function MessageFile({ file }) {
  return (
    <Message isFile={true}>
      <div className="flex space-x-2">
        <div>
          <div className="h-[40px] w-[35px] bg-red-500 rounded flex items-center justify-center px-1">
            <p className="font-black text-white text-xs">
              FILE
            </p>
          </div>
        </div>
        <div>
          <Text className="font-medium">
            {file.filename}
          </Text>
          <Text className="text-xs">
            Arquivo
          </Text>
        </div>
      </div>
    </Message>
  )
}

function ProgressDone() {
  return (
    <div className="relative">
      <div className="relative h-[10px] w-full bg-[#00ccff]">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 shadow w-[40px] h-[40px] rounded-full bg-[#00ccff] flex justify-center items-center">
          <CheckIcon style={{ color: 'white' }} />
        </div>
      </div>
    </div>
  )
}

function ProgressWaiting() {
  const theme = useTheme()

  return (
    <div className="relative">
      <div
        className={clsx('relative h-[10px] w-full bg-opacity-[0.1]', {
          'bg-white': theme.isDark,
          'bg-gray-900': !theme.isDark
        })}
      >
        <div className={clsx('absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[40px] h-[40px] rounded-full', {
          'shadow bg-[#67676a]': theme.isDark,
          'bg-[#cacaca]': !theme.isDark
        })}>

        </div>
      </div>
    </div>
  )
}

function sanitizeSgpFileUrl(url) {
  const handledURL = String(url).startsWith('http') ?  url : `http://${url}`
  return handledURL.replace('/.pdf', '.pdf')
}

// Opções de status disponíveis para filtro
const STATUS_OPTIONS = [
  { value: 'success', label: 'Enviado', color: '#059669', icon: <CheckIcon style={{ fontSize: 16 }} /> },
  { value: 'queue', label: 'Em fila', color: '#6b7280', icon: <WatchLaterIcon style={{ fontSize: 16 }} /> },
  { value: 'erro', label: 'Com erro', color: '#eb3c3c', icon: <ReportProblemIcon style={{ fontSize: 16 }} /> },
  { value: 'antiban_delayed', label: 'Anti-ban', color: '#f59e0b', icon: <WatchLaterIcon style={{ fontSize: 16 }} /> },
  { value: 'suppressed', label: 'Opt-out', color: '#64748b', icon: <GppMaybeIcon style={{ fontSize: 16 }} /> },
]

function MessagesFilterModal({ isOpen = false, onClose, onFilter, phoneLike = '', messageLike = '', statusFilter = '' }) {
  const theme = useTheme()
  const [phone, setPhone] = useState(phoneLike)
  const [message, setMessage] = useState(messageLike)
  const [selectedStatus, setSelectedStatus] = useState(statusFilter)

  // Sincroniza quando o drawer reabre com valores externos diferentes
  useEffect(() => {
    if (isOpen) {
      setPhone(phoneLike)
      setMessage(messageLike)
      setSelectedStatus(statusFilter)
    }
  }, [isOpen])

  const handleOnFilter = () => {
    const ml = message
    const pl = phone.replace(/\D/g, '')
    setPhone(pl)
    onFilter({
      phoneLike: pl,
      messageLike: ml,
      status: selectedStatus
    })
    onClose()
  }

  const handleClearFilters = () => {
    setPhone('')
    setMessage('')
    setSelectedStatus('')
    onFilter({ phoneLike: '', messageLike: '', status: '' })
    onClose()
  }

  const hasActiveFilters = phone || message || selectedStatus

  return (
    <Drawer
      open={isOpen}
      anchor="right"
      onClose={onClose}
    >
      <div className="w-[100vw] md:w-[750px]">
        <div className="p-4 md:p-12">
          <div className="flex items-center justify-between space-x-4">
            <div className="flex items-center space-x-2">
              <FilterAltIcon style={{ color: theme.palette.primary.main, fontSize: 30 }} />
              <Text className="text-xl font-bold">Filtro de mensagens</Text>
            </div>
            <button onClick={onClose}>
              <CloseIcon />
            </button>
          </div>
          <div className="mt-10">
            <div>
              <div>
                <Text className="text-lg font-medium">Status da mensagem</Text>
                <Text className="text-sm opacity-60 mt-1">Selecione um status para filtrar</Text>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {STATUS_OPTIONS.map(opt => {
                  const isActive = selectedStatus === opt.value
                  return (
                    <Chip
                      key={opt.value}
                      icon={opt.icon}
                      label={opt.label}
                      clickable
                      onClick={() => setSelectedStatus(isActive ? '' : opt.value)}
                      sx={{
                        fontWeight: isActive ? 700 : 500,
                        fontSize: '0.85rem',
                        px: 0.5,
                        border: `1.5px solid ${isActive ? opt.color : 'transparent'}`,
                        backgroundColor: isActive
                          ? `${opt.color}22`
                          : theme.isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                        color: isActive ? opt.color : 'inherit',
                        '& .MuiChip-icon': {
                          color: isActive ? opt.color : 'inherit'
                        },
                        '&:hover': {
                          backgroundColor: `${opt.color}33`,
                        }
                      }}
                    />
                  )
                })}
              </div>
            </div>
            <div className="mt-10">
              <div>
                <Text className="text-lg font-medium">Critérios de busca</Text>
              </div>
              <div className="mt-6">
                <div>
                  <div className="flex items-center space-x-1">
                    <FilterAltIcon style={{ color: theme.palette.primary.main }} />
                    <Text className="text-sm">Número whatsapp</Text>
                  </div>
                </div>
                <div className="mt-3">
                  <TextFieldCustom
                    size="large"
                    placeholder="Ex: 11999888777"
                    helperText="Informe o número whatsapp a ser buscado"
                    label="Número whatsapp"
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                  />
                </div>
              </div>
              <div className="mt-8">
                <div>
                  <div className="flex items-center space-x-1">
                    <FilterAltIcon style={{ color: theme.palette.primary.main }} />
                    <Text className="text-sm">Trecho do conteúdo da mensagem</Text>
                  </div>
                </div>
                <div className="mt-3">
                  <TextFieldCustom
                    size="large"
                    placeholder="Ex: nome do contato, código pix, etc"
                    helperText="Nome do contato, código pix, link, etc, qualquer palavra ou código que contenha na mensagem enviada"
                    label="Conteúdo a ser buscado"
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div className="mt-16 flex items-center space-x-4">
              <Button onClick={handleOnFilter} startIcon={<FilterAltIcon />}>
                APLICAR FILTROS
              </Button>
              {
                hasActiveFilters &&
                <Button
                  onClick={handleClearFilters}
                  startIcon={<CloseIcon />}
                  color="inherit"
                  sx={{ opacity: 0.7 }}
                >
                  LIMPAR FILTROS
                </Button>
              }
            </div>
          </div>
        </div>
      </div>
    </Drawer>
  )
}
